/**
 * Router DietaGest.
 *
 * Strategia di redirect:
 * - Pubblico: /login, /forgot-password
 * - Forced first-login (mustChangePassword): /first-login
 * - Staff senza MFA enrollata: /mfa/enroll
 * - Tutti gli altri (autenticati e onboard completo): /app/*
 *
 * Il <ProtectedRoute> centralizza le precedenze sopra in modo che ogni
 * navigazione manuale (es. tentativo di skippare /first-login) venga
 * intercettata e rediretta.
 */

import { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isStaffRole, type UserRole } from '@shared/types/auth';
import { LoginPage } from '../pages/auth/LoginPage';
import { ForgotPasswordPage } from '../pages/auth/ForgotPasswordPage';
import { FirstLoginPage } from '../pages/auth/FirstLoginPage';
import { MfaEnrollFlow } from '../components/auth/MfaEnrollFlow';
import { AppShell } from '../components/layout/AppShell';
import { DashboardPage } from '../pages/dashboard/DashboardPage';
import { ComunicazioniPlaceholderPage } from '../pages/comunicazioni/ComunicazioniPlaceholderPage';
import { StatistichePlaceholderPage } from '../pages/statistiche/StatistichePlaceholderPage';

// PR5 - lazy load delle pagine pesanti per ridurre bundle iniziale
const PazientiPage = lazy(() =>
  import('../pages/pazienti/PazientiPage').then((m) => ({ default: m.PazientiPage }))
);
// PR-G3a — libreria template piani alimentari (chiude DEBT-2026-07-29b)
const PianiTemplateListPage = lazy(() =>
  import('../pages/piani/PianiTemplateListPage').then((m) => ({
    default: m.PianiTemplateListPage,
  }))
);
const PianiTemplateDetailPage = lazy(() =>
  import('../pages/piani/PianiTemplateDetailPage').then((m) => ({
    default: m.PianiTemplateDetailPage,
  }))
);
// PR-G3b Fase 3 — editor TipTap (chunk editor-piani via lazy)
const PianiTemplateEditorPage = lazy(() =>
  import('../pages/piani/PianiTemplateEditorPage').then((m) => ({
    default: m.PianiTemplateEditorPage,
  }))
);
const FascicoloPazientePage = lazy(() =>
  import('../pages/pazienti/FascicoloPazientePage').then((m) => ({
    default: m.FascicoloPazientePage,
  }))
);
// PR-G4a — dettaglio piano paziente (sola lettura). Chunk separato dal
// fascicolo: la guardia anti-TipTap in check-bundle-size.mjs lo scandisce.
const PianoPazienteDetailPage = lazy(() =>
  import('../pages/pazienti/PianoPazienteDetailPage').then((m) => ({
    default: m.PianoPazienteDetailPage,
  }))
);
// PR-G4b — editor del piano paziente. Chunk PROPRIO, distinto dal
// dettaglio: e' l'unica pagina del fascicolo che tira dentro TipTap, e
// tenerla separata e' cio' che permette alla denylist anti-TipTap di
// restare stretta sugli altri chunk del fascicolo.
const PianoPazienteEditorPage = lazy(() =>
  import('../pages/pazienti/PianoPazienteEditorPage').then((m) => ({
    default: m.PianoPazienteEditorPage,
  }))
);
const ImpostazioniPage = lazy(() =>
  import('../pages/impostazioni/ImpostazioniPage').then((m) => ({
    default: m.ImpostazioniPage,
  }))
);
const OperatoriListPage = lazy(() =>
  import('../pages/impostazioni/OperatoriListPage').then((m) => ({
    default: m.OperatoriListPage,
  }))
);
const OperatoreFormPage = lazy(() =>
  import('../pages/impostazioni/OperatoreFormPage').then((m) => ({
    default: m.OperatoreFormPage,
  }))
);
const PartiteIvaListPage = lazy(() =>
  import('../pages/impostazioni/PartiteIvaListPage').then((m) => ({
    default: m.PartiteIvaListPage,
  }))
);
const PartitaIvaFormPage = lazy(() =>
  import('../pages/impostazioni/PartitaIvaFormPage').then((m) => ({
    default: m.PartitaIvaFormPage,
  }))
);
const TipologieColoriPage = lazy(() =>
  import('../pages/impostazioni/TipologieColoriPage').then((m) => ({
    default: m.TipologieColoriPage,
  }))
);
const PromemoriaConfigPage = lazy(() =>
  import('../pages/impostazioni/PromemoriaConfigPage').then((m) => ({
    default: m.PromemoriaConfigPage,
  }))
);
const PromemoriaLogPage = lazy(() =>
  import('../pages/impostazioni/PromemoriaLogPage').then((m) => ({
    default: m.PromemoriaLogPage,
  }))
);
// PR8a - Agenda globale (chunk dedicato con react-big-calendar).
const AgendaPage = lazy(() =>
  import('../pages/agenda/AgendaPage').then((m) => ({ default: m.AgendaPage }))
);
// PR10 - Registro fatture (chunk dedicato per separare pdf-lib lato CF)
const RegistroFatturePage = lazy(() =>
  import('../pages/fatturazione/RegistroFatturePage').then((m) => ({
    default: m.RegistroFatturePage,
  }))
);

function LazyFallback() {
  return (
    <div className="p-6 text-text-secondary font-body-md">Caricamento...</div>
  );
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen bg-bg-app flex items-center justify-center">
      <div className="text-text-secondary font-body-md">Caricamento…</div>
    </div>
  );
}

interface GuardedProps {
  children: React.ReactNode;
  /** Se true, l'utente deve essere autenticato. */
  requireAuth?: boolean;
  /** Se true, l'utente NON deve essere autenticato (es. /login). */
  redirectIfAuth?: boolean;
  /**
   * PR8a (MOD 1) — whitelist ruoli ammessi per questa route.
   *
   * BACKWARD COMPATIBILITY: se `undefined`/non passata, il guard si
   * comporta ESATTAMENTE come prima di PR8a (nessun controllo ruolo).
   * Tutti i test pre-PR8a rimangono verdi senza modifiche.
   *
   * Se presente, dopo aver passato auth/mustChangePassword/MFA, se
   * `role` non e' nella lista, redirect:
   *  - PAZIENTE -> /app/pazienti/{uid} (proprio fascicolo)
   *  - altri    -> /app (dashboard)
   *
   * Usato per le pagine staff-only (Agenda globale, Impostazioni).
   */
  allowedRoles?: ReadonlyArray<UserRole>;
}

export function RouteGuard({
  children,
  requireAuth,
  redirectIfAuth,
  allowedRoles,
}: GuardedProps) {
  const { user, role, mfaEnrolled, mustChangePassword, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;

  if (redirectIfAuth && user) {
    return <Navigate to="/app" replace />;
  }

  if (requireAuth && !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (user && mustChangePassword && location.pathname !== '/first-login') {
    return <Navigate to="/first-login" replace />;
  }

  if (
    user &&
    !mustChangePassword &&
    isStaffRole(role) &&
    !mfaEnrolled &&
    location.pathname !== '/mfa/enroll'
  ) {
    return <Navigate to="/mfa/enroll" replace />;
  }

  // Simmetrica della rule precedente: se l'utente è su /mfa/enroll
  // ma in realtà ha già completato l'enrollment (o è bypassato in dev),
  // mandalo all'app. Senza questa il browser resta bloccato sulla pagina
  // di enrollment finché non riavvia/clear-storage.
  if (
    user &&
    !mustChangePassword &&
    isStaffRole(role) &&
    mfaEnrolled &&
    location.pathname === '/mfa/enroll'
  ) {
    return <Navigate to="/app" replace />;
  }

  // PR8a (MOD 1) — RBAC role-allowlist. Eseguito DOPO tutti i check
  // precedenti per non perturbare il comportamento esistente: i test
  // pre-PR8a non passano `allowedRoles`, quindi questo branch e' no-op.
  if (
    user &&
    !mustChangePassword &&
    allowedRoles &&
    allowedRoles.length > 0 &&
    role &&
    !allowedRoles.includes(role)
  ) {
    if (role === 'PAZIENTE') {
      return <Navigate to={`/app/pazienti/${user.uid}`} replace />;
    }
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={
            <RouteGuard redirectIfAuth>
              <LoginPage />
            </RouteGuard>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <RouteGuard redirectIfAuth>
              <ForgotPasswordPage />
            </RouteGuard>
          }
        />
        <Route
          path="/first-login"
          element={
            <RouteGuard requireAuth>
              <FirstLoginPage />
            </RouteGuard>
          }
        />
        <Route
          path="/mfa/enroll"
          element={
            <RouteGuard requireAuth>
              <MfaEnrollFlow />
            </RouteGuard>
          }
        />
        <Route
          path="/app"
          element={
            <RouteGuard requireAuth>
              <AppShell />
            </RouteGuard>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route
            path="agenda"
            element={
              <RouteGuard
                requireAuth
                allowedRoles={['TITOLARE', 'DIETOLOGA', 'SEGRETERIA', 'ADMIN']}
              >
                <Suspense fallback={<LazyFallback />}>
                  <AgendaPage />
                </Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="pazienti"
            element={
              <Suspense fallback={<LazyFallback />}>
                <PazientiPage />
              </Suspense>
            }
          />
          {/* PR5 - Fascicolo paziente nested routes */}
          <Route
            path="pazienti/:pazienteId"
            element={
              <Suspense fallback={<LazyFallback />}>
                <FascicoloPazientePage />
              </Suspense>
            }
          />
          {/* PR-G4a — piani paziente: rotte PIU' SPECIFICHE di :tabName,
              altrimenti `piani/:pianoId` verrebbe assorbito come tabName.
              ADMIN escluso (dato sanitario art.9): il gate UI resta nel
              componente per dietologa non assegnata e per i test. */}
          {/* PR-G4b — PRIMA di `piani/:pianoId`, altrimenti `modifica`
              non sarebbe mai raggiunta. SEGRETERIA ESCLUSA, a differenza
              delle rotte di lettura qui sotto: legge e stampa, non
              modifica. Il secondo cancello (dietologa assegnata) vive
              nel componente, che conosce il paziente. */}
          <Route
            path="pazienti/:pazienteId/piani/:pianoId/modifica"
            element={
              <RouteGuard
                requireAuth
                allowedRoles={['TITOLARE', 'DIETOLOGA']}
              >
                <Suspense fallback={<LazyFallback />}>
                  <PianoPazienteEditorPage />
                </Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="pazienti/:pazienteId/piani/:pianoId"
            element={
              <RouteGuard
                requireAuth
                allowedRoles={['TITOLARE', 'DIETOLOGA', 'SEGRETERIA']}
              >
                <Suspense fallback={<LazyFallback />}>
                  <PianoPazienteDetailPage />
                </Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="pazienti/:pazienteId/piani"
            element={
              <RouteGuard
                requireAuth
                allowedRoles={['TITOLARE', 'DIETOLOGA', 'SEGRETERIA']}
              >
                <Suspense fallback={<LazyFallback />}>
                  <FascicoloPazientePage />
                </Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="pazienti/:pazienteId/:tabName"
            element={
              <Suspense fallback={<LazyFallback />}>
                <FascicoloPazientePage />
              </Suspense>
            }
          />
          <Route
            path="piani"
            element={
              <RouteGuard
                requireAuth
                allowedRoles={['TITOLARE', 'DIETOLOGA', 'SEGRETERIA', 'ADMIN']}
              >
                <Suspense fallback={<LazyFallback />}>
                  <PianiTemplateListPage />
                </Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="piani/:templateId/modifica"
            element={
              <RouteGuard
                requireAuth
                allowedRoles={['TITOLARE', 'DIETOLOGA', 'ADMIN']}
              >
                <Suspense fallback={<LazyFallback />}>
                  <PianiTemplateEditorPage />
                </Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="piani/:templateId"
            element={
              <RouteGuard
                requireAuth
                allowedRoles={['TITOLARE', 'DIETOLOGA', 'SEGRETERIA', 'ADMIN']}
              >
                <Suspense fallback={<LazyFallback />}>
                  <PianiTemplateDetailPage />
                </Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="fatturazione"
            element={
              <RouteGuard
                requireAuth
                allowedRoles={['TITOLARE', 'DIETOLOGA', 'SEGRETERIA']}
              >
                <Suspense fallback={<LazyFallback />}>
                  <RegistroFatturePage />
                </Suspense>
              </RouteGuard>
            }
          />
          <Route
            path="comunicazioni"
            element={<ComunicazioniPlaceholderPage />}
          />
          <Route
            path="statistiche"
            element={<StatistichePlaceholderPage />}
          />
          <Route
            path="impostazioni"
            element={
              <Suspense fallback={<LazyFallback />}>
                <ImpostazioniPage />
              </Suspense>
            }
          />
          <Route
            path="impostazioni/operatori"
            element={
              <Suspense fallback={<LazyFallback />}>
                <OperatoriListPage />
              </Suspense>
            }
          />
          <Route
            path="impostazioni/operatori/nuovo"
            element={
              <Suspense fallback={<LazyFallback />}>
                <OperatoreFormPage />
              </Suspense>
            }
          />
          <Route
            path="impostazioni/operatori/:operatoreId"
            element={
              <Suspense fallback={<LazyFallback />}>
                <OperatoreFormPage />
              </Suspense>
            }
          />
          <Route
            path="impostazioni/partite-iva"
            element={
              <Suspense fallback={<LazyFallback />}>
                <PartiteIvaListPage />
              </Suspense>
            }
          />
          <Route
            path="impostazioni/partite-iva/nuova"
            element={
              <Suspense fallback={<LazyFallback />}>
                <PartitaIvaFormPage />
              </Suspense>
            }
          />
          <Route
            path="impostazioni/partite-iva/:pIvaId"
            element={
              <Suspense fallback={<LazyFallback />}>
                <PartitaIvaFormPage />
              </Suspense>
            }
          />
          <Route
            path="impostazioni/tipologie"
            element={
              <Suspense fallback={<LazyFallback />}>
                <TipologieColoriPage />
              </Suspense>
            }
          />
          <Route
            path="impostazioni/promemoria"
            element={
              <Suspense fallback={<LazyFallback />}>
                <PromemoriaConfigPage />
              </Suspense>
            }
          />
          <Route
            path="impostazioni/promemoria/log"
            element={
              <Suspense fallback={<LazyFallback />}>
                <PromemoriaLogPage />
              </Suspense>
            }
          />
        </Route>
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppRouter;
